import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  ViewEncapsulation,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { CommonModule } from '@angular/common';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { buildAzureStoredPhotoUrl } from 'apps/account/src/account-shared/pipes/azure-stored-photo.util';
import { CurationBackgroundImageDirective } from '../../../../features/stories/curation-card/background-image.directive';
import {
  CurationFullNamePipe,
  CurationTruncatePipe,
} from '../../../../features/stories/curation-card/curation-card.pipes';
import { TranslateModule } from '@ngx-translate/core';

/**
 * Curation share-image editor panel: live preview + background/profile/name/quote/caption
 * options. Presentational — sharing/property/guest arrive as @Input()s; the two footer
 * actions are @Output()s. Part of the dead share chain (no in-app caller).
 */
@Component({
    selector: 'ft-curation-share-ng',
    imports: [
        CommonModule,
        CurationBackgroundImageDirective,
        CurationFullNamePipe,
        CurationTruncatePipe,
        TranslateModule,
    ],
    encapsulation: ViewEncapsulation.None,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './ft-curation-share.component.html'
})
export class FtCurationShareComponent {
    readonly sharing = input<any>();
    readonly property = input<any>();
    readonly guest = input<any>();
    readonly quoteSize = input<number | undefined>();
    readonly captionSize = input<number | undefined>();
    readonly shareMessageLimit = input<number | undefined>();

    /** v1 `done()` (ft-modal) → on-done="createShareImage". */
    readonly previewPost = output<void>();
    /** v1 `cancel()` (ft-modal) → on-cancel="showCurationModal". */
    readonly cancelClicked = output<void>();

    /** v1 `ft-click-class toggle-class="active"` on the background-photo dropdown. */
    protected readonly dropdownOpen = signal(false);

    /**
     * v1 `class="share-photo-preview {{sharing.network.title}}"` + the `ng-class` booleans.
     * Built as a single NgClass OBJECT (the network-title is a dynamic key) — NgClass's
     * array form cannot mix strings with an object literal (it calls `.trim()` on every
     * array element → throws on the object → aborts change detection for the whole panel).
     */
    protected get previewClasses(): Record<string, boolean> {
        const sharing = this.sharing();
        const title = sharing?.network?.title;
        const cls: Record<string, boolean> = {
            'quote-only': !sharing?.backgroundIsGuestPhoto,
            'show-quote': !!sharing?.isShowGuestQuote,
            'show-caption': !!sharing?.isShowPhotoCaption,
            landscape: !!sharing?.isLandscape,
            portrait: !sharing?.isLandscape,
            'ft-rtl': !!sharing?.isRtl,
        };
        if (title) cls[title] = true;
        return cls;
    }
    /** v1 `modalForm.message.$pristine` — the message textarea hasn't been edited yet. */
    protected readonly messagePristine = signal(true);

    private readonly parse: any = inject(ParseService);

    private readonly environment = inject<Environment>(ENVIRONMENT_TOKEN);

    protected onMessageInput(value: string): void {
        this.messagePristine.set(false);
        if (this.sharing()) this.sharing().message = value;
    }

    /** Native port of v1 `ftAzureStoredPhoto` filter (dropdown thumbnail). */
    protected azurePhoto(photo: any, size: string): string | null {
        return photo ? buildAzureStoredPhotoUrl(photo, size, this.environment.AzureCdnBaseUrl) : null;
    }

    /** The message textarea is shown only for non-instagram, non-facebook networks (v1 `ng-if`).
     * v1's `modalForm.message` form control exists only then → the `ft-field-error`/`$invalid`
     * expressions are null-safe-undefined (falsy) when it's hidden. Gate on this to match. */
    protected messageVisible(): boolean {
        return !this.isInstagram(this.sharing()?.network) && !this.isFacebook(this.sharing()?.network);
    }

    /** message control is `required`; valid when non-empty. */
    protected messageValid(): boolean {
        return !!(this.sharing() && this.sharing().message);
    }

    /** v1 `ng-disabled="modalForm.$invalid"` — the form has only the (conditionally shown) required
     * message control, so it's invalid iff that control is visible AND empty. */
    protected isPreviewDisabled(): boolean {
        return this.messageVisible() && !this.messageValid();
    }

    // --- helpers verbatim from curationModal.directive.js ---

    protected isInstagram(network: any): boolean {
        return network && network.socialNetworkType == 5;
    }
    protected isFacebook(network: any): boolean {
        return network && network.socialNetworkType == 1;
    }
    protected isLinkedin(network: any): boolean {
        return network && network.socialNetworkType == 3;
    }

    protected hasPhoto(): boolean {
        const g = this.guest();
        return g && g.poststay && g.poststay.photo !== undefined;
    }
    protected hasCaption(): boolean {
        const g = this.guest();
        return g && g.poststay && g.poststay.photo && g.poststay.photo.caption && g.poststay.photo.caption.length > 0;
    }
    protected hasPositiveQuote(): boolean {
        const g = this.guest();
        return (
            g && g.poststay && g.poststay.quote && g.poststay.quote.notesPositive && this.parse.bool(g.poststay.quote.isPositive)
        );
    }
    protected isHotelRatedNegativeQuote(): boolean {
        const g = this.guest();
        return (
            g && g.poststay && g.poststay.quote && g.poststay.quote.internalInfo && g.poststay.quote.internalInfo['rating'] <= 1
        );
    }
    protected isHotelRatedNegativeCaption(): boolean {
        const g = this.guest();
        return (
            g &&
            g.poststay &&
            g.poststay.photo &&
            g.poststay.photo.internalInfo &&
            g.poststay.photo.internalInfo['captionRating'] <= 1
        );
    }
}
